import type { PrescribedProduct, RefillPrescribedProduct } from '@/types';
import { getCancelledOrDeletedStatus } from '@/utils/prescription';
import type { PatientPrescriptionStatus } from '@montugroup/pms-api-contracts';
import { useMemo } from 'react';
import type { PrescriptionMedicationsTableRow } from '../PrescriptionMedicationsTable.types';

const sortPrescribedProductByActive = (a: PrescribedProduct, b: PrescribedProduct) => {
  return a.active === b.active ? 0 : a.active ? -1 : 1;
};

const getProductStatus = (
  product: PrescribedProduct,
  prescriptionStatus: PatientPrescriptionStatus
): PatientPrescriptionStatus => {
  return product.active ? prescriptionStatus : getCancelledOrDeletedStatus(product.prescriptionProductCode);
};

const getRefillProductStatus = (
  product: RefillPrescribedProduct,
  prescriptionStatus: PatientPrescriptionStatus
): PatientPrescriptionStatus => {
  return product.active ? prescriptionStatus : getCancelledOrDeletedStatus(product.refillPrescriptionProductCode);
};

export const useMedicationsTableData = ({
  prescriptionStatus,
  prescribedProducts,
  expandedRows
}: {
  prescriptionStatus: PatientPrescriptionStatus;
  prescribedProducts: PrescribedProduct[] | undefined;
  expandedRows: number[];
}) => {
  const expandedData: PrescriptionMedicationsTableRow[] = useMemo(
    () =>
      prescribedProducts
        ?.sort(sortPrescribedProductByActive)
        .map((prescribedProduct, index): PrescriptionMedicationsTableRow[] | PrescriptionMedicationsTableRow => {
          const evenRow = index % 2 === 0;

          if (!expandedRows.includes(prescribedProduct.id)) {
            return {
              ...prescribedProduct,
              altRowColor: evenRow,
              status: getProductStatus(prescribedProduct, prescriptionStatus)
            };
          }

          const childRows: PrescriptionMedicationsTableRow[] = prescribedProduct.refillPrescribedProducts
            .map(
              (refillProduct): PrescriptionMedicationsTableRow => ({
                id: refillProduct.id,
                productId: refillProduct.prescribedProductId,
                active: !refillProduct.active,
                quantity: refillProduct.quantity,
                interval: refillProduct.interval?.toString() ?? null,
                repeats: refillProduct.repeats,
                formulationId: null,
                strainId: null,
                dosage: null,
                name: refillProduct.refillPrescriptionProductCode ?? '',
                formulationName: '',
                thcStrengthForDisplay: null,
                cbdStrengthForDisplay: null,
                otherProduct: null,
                prescriptionProductCode: refillProduct.refillPrescriptionProductCode ?? '',
                createdDate: refillProduct.createdDate ?? '',
                refillPrescribedProducts: [],
                isRefillScript: true,
                status: getRefillProductStatus(refillProduct, prescriptionStatus),
                altRowColor: evenRow
              })
            )
            .sort(sortPrescribedProductByActive)
            .map((product, index, products) => ({
              ...product,
              removeBorder: index < products.length - 1
            }));

          return [
            {
              ...prescribedProduct,
              status: getProductStatus(prescribedProduct, prescriptionStatus),
              altRowColor: evenRow,
              removeBorder: true,
              expanded: true
            },
            ...childRows
          ];
        })
        .flat() ?? [],
    [prescribedProducts, expandedRows, prescriptionStatus]
  );

  return expandedData;
};
